import {
	Box,
	Divider,
	Stack,
	Typography,
	Button,
	IconButton,
} from "@mui/material";
import {
	DeviceState,
	DeviceStatus,
} from "Infrastructure/Models/Messages/Dto/DeviceStatus";
import { DeviceForm } from "Infrastructure/Utils/Nsure/DeviceUtils";
import { FunctionComponent, useState, useEffect, useRef } from "react";
import InfoWarningsActionSave, {
	getNewFormData as getNewSaveFormData,
} from "./InfoWarningActionSave";
import { HubConnection } from "@microsoft/signalr";
import { CloseDialogHandler } from "Infrastructure/Providers/DialogProvider/DialogProvider";
import InfoWarningsActionDelete from "./InfoWarningActionDelete";
import {
	InfoWarningCreateMessage,
	InfoWarningDeleteByDescriptionMessage,
} from "Infrastructure/Models/Messages/Messages";
import DeviceFormComponent from "../DeviceFormComponent";
import { ActionHistorySelect } from "../ActionHistorySelect";
import { ActionHistoryDto } from "Infrastructure/Api/Api";
import { ActionType } from "Infrastructure/Api/ApiCustom";
import { useSnackbar } from "Infrastructure/Providers/SnackbarProvider/SnackbarProvider";
import { InfoWarningSaveFormData } from "./InfoWarningActionSave";
import {
	getNewFormData as getNewDeleteFormData,
	InfoWarningDeleteFormData,
} from "./InfoWarningActionDelete";
import { parseISO } from "date-fns";
import ClearIcon from "@mui/icons-material/Clear";

type Props = {
	deviceStatuses: DeviceStatus[] | null;
	deviceStatus?: DeviceStatus | undefined;
	hubConnection: HubConnection | null;
	closeDialog: CloseDialogHandler;
	onCreate: (message: InfoWarningCreateMessage) => void;
	onDelete: (message: InfoWarningDeleteByDescriptionMessage) => void;
};

// Utility: Map filteredDeviceNames to deviceForm
function updateDeviceFormWithFilteredNames(
	deviceForm: DeviceForm,
	filteredDeviceNames: string[] = []
) {
	return {
		...deviceForm,
		devices: deviceForm.devices.map((device) => ({
			...device,
			isChecked: filteredDeviceNames.includes(device.name),
		})),
	};
}

// Utility: Map infoWarning to InfoWarningSaveFormData
function mapInfoWarningToSaveFormData(
	infoWarning: any,
	notifyClientByEmail: boolean
): InfoWarningSaveFormData {
	return {
		isValidationShown: false,
		text: { value: infoWarning.Text, isValid: true },
		notifyClientByEmail: { value: notifyClientByEmail, isValid: true },
		validFrom: {
			value: infoWarning.ValidFrom ? parseISO(infoWarning.ValidFrom) : null,
			isValid: true,
		},
		validTo: {
			value: infoWarning.ValidTo ? parseISO(infoWarning.ValidTo) : null,
			isValid: true,
		},
		url: { value: infoWarning.URL, isValid: true },
		type: { value: infoWarning.Type, isValid: true },
		productTypeId: {
			value: infoWarning.ProductTypeID?.split(";") ?? [],
			isValid: true,
		},
		description: { value: infoWarning.Description, isValid: true },
		visibility: { value: infoWarning.Visibility, isValid: true },
	};
}

// Utility: Map delete message to InfoWarningDeleteFormData
function mapDeleteMessageToDeleteFormData(
	message: any
): InfoWarningDeleteFormData {
	return {
		isValidationShown: false,
		description: { value: message.Description, isValid: true },
		notifyClientByEmail: { value: message.NotifyClientByEmail, isValid: true },
	};
}

const InfoWarningsActions: FunctionComponent<
	Props & {
		setSaveFormData: (data: InfoWarningSaveFormData) => void;
		setDeleteFormData: (data: InfoWarningDeleteFormData) => void;
		saveFormData: InfoWarningSaveFormData;
		deleteFormData: InfoWarningDeleteFormData;
	}
> = (props) => {
	const {
		deviceStatuses,
		deviceStatus,
		hubConnection,
		closeDialog,
		onCreate,
		onDelete,
		setSaveFormData,
		setDeleteFormData,
		saveFormData,
		deleteFormData,
	} = props;

	const showSnackbar = useSnackbar();
	const [selectedHistory, setSelectedHistory] = useState<ActionHistoryDto | null>(null);
	const [isHistoryLoaded, setIsHistoryLoaded] = useState(false);
	const justLoadedHistory = useRef(false);

	// Wrap setForm to clear isHistoryLoaded on any change
	const setFormWithHistoryCheck = (
		updater: React.SetStateAction<DeviceForm>
	) => {
		setForm((prev: DeviceForm) => {
			const next = typeof updater === "function" ? updater(prev) : updater;
			if (isHistoryLoaded && !justLoadedHistory.current) {
				setIsHistoryLoaded(false);
				setSelectedHistory(null);
			}
			return next;
		});
	};

	if (!deviceStatuses || deviceStatuses?.length === 0) {
		return (
			<Typography variant="caption">Žádné instance k dispozici</Typography>
		);
	}

	const [form, setForm] = useState<DeviceForm>({
		devices: !!deviceStatus
			? [
					{
						name: deviceStatus.deviceName,
						isChecked: true,
						isDisabled: false,
						deviceStatus,
					},
			  ]
			: deviceStatuses.map((x) => ({
					name: x.deviceName,
					isChecked: false,
					isDisabled:
						x.deviceState !== DeviceState.Connected || x.isSelfInsurance,
					deviceStatus: x,
			  })),
	});

	// biome-ignore lint/correctness/useExhaustiveDependencies: form, saveFormData, deleteFormData are intentional change triggers
	useEffect(() => {
		if (isHistoryLoaded) {
			if (justLoadedHistory.current) {
				justLoadedHistory.current = false;
			} else {
				// Clear history when any form data changes manually
				setIsHistoryLoaded(false);
				setSelectedHistory(null);
			}
		}
	}, [form, saveFormData, deleteFormData, isHistoryLoaded]);

	const clearAllData = () => {
		setForm((prev) => ({
			...prev,
			devices: prev.devices.map((d) => ({ ...d, isChecked: false })),
		}));
		setSaveFormData(getNewSaveFormData());
		setDeleteFormData(getNewDeleteFormData());
		setSelectedHistory(null);
		setIsHistoryLoaded(false);
	};

	return (
		<Stack>
			<Box>
				<Stack
					direction="row"
					spacing={1}
					alignItems="center"
					width="900px"
					justifySelf="end"
				>
					<ActionHistorySelect
						device={deviceStatus?.deviceName}
						actionTypes={[
							ActionType.InfoWarningCreate,
							ActionType.InfoWarningDeleteByDescription,
						]}
						value={selectedHistory}
						onSelect={(action) => {
							clearAllData();
							setSelectedHistory(action);
							if (action) {
								try {
									const parsed = JSON.parse(action.messageJson);
									// Use PascalCase keys as in your JSON
									const infoWarning = parsed.InfoWarning;
									const filteredDeviceNames = parsed.FilteredDeviceNames || [];
									// Update device form
									setFormWithHistoryCheck((prev: DeviceForm) =>
										updateDeviceFormWithFilteredNames(prev, filteredDeviceNames)
									);
									if (action.actionType === ActionType.InfoWarningCreate) {
										setSaveFormData(
											mapInfoWarningToSaveFormData(
												infoWarning,
												infoWarning.NotifyClientByEmail
											)
										);
									} else if (
										action.actionType ===
										ActionType.InfoWarningDeleteByDescription
									) {
										setDeleteFormData(mapDeleteMessageToDeleteFormData(parsed));
									}
									setIsHistoryLoaded(true);
									justLoadedHistory.current = true;
								} catch {
									showSnackbar("Nepodařilo se načíst historickou akci.", "error");
								}
							}
						}}
						disabled={isHistoryLoaded}
					/>
					<IconButton
						size="small"
						onClick={clearAllData}
						disabled={!isHistoryLoaded}
					>
						<ClearIcon sx={{ fontSize: "1.2rem" }} />
					</IconButton>
				</Stack>
			</Box>
			{!deviceStatus && (
				<Box mb={1}>
					<Typography variant="h6" component="div">
						Instance
					</Typography>
					<Divider />
					<Box ml={1.5} mt={1}>
						<DeviceFormComponent
							deviceStatuses={deviceStatuses}
							deviceStatus={deviceStatus}
							form={form}
							setForm={setFormWithHistoryCheck}
							showInfoWarningColumns={true}
						/>
					</Box>
				</Box>
			)}
			<Stack>
				<Box>
					<Typography variant="h6" component="div">
						Akce
					</Typography>
					<Divider />
				</Box>
				<InfoWarningsActionSave
					deviceForm={form}
					hubConnection={hubConnection}
					closeDialog={closeDialog}
					onCreate={onCreate}
					formData={saveFormData}
					setFormData={setSaveFormData}
				/>
				<InfoWarningsActionDelete
					deviceForm={form}
					hubConnection={hubConnection}
					closeDialog={closeDialog}
					onDelete={onDelete}
					formData={deleteFormData}
					setFormData={setDeleteFormData}
				/>
			</Stack>
		</Stack>
	);
};

export default InfoWarningsActions;
